import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  PlusIcon,
  ArrowDownTrayIcon,
  EllipsisVerticalIcon,
  MagnifyingGlassIcon,
  DocumentIcon,
  PresentationChartLineIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { getSupabaseClient } from '../lib/supabase';

interface Resource {
  id: string;
  name: string;
  description: string | null;
  author: string | null;
  category: string | null;
  tags: string[] | null;
  file_name: string;
  file_path: string;
  file_size: number;
  file_type: string | null;
  mime_type: string;
  downloads: number;
  uploaded_at: string;
  uploaded_by: string;
  thumbnail_url: string | null;
  content_url: string | null;
  is_active: boolean;
  seeker_viewable: boolean | null;
  parent_id: string | null;
}

const determineFileType = (filename: string) => {
  const ext = filename.split('.').pop()?.toLowerCase();
  if (!ext) return 'Document';
  if (ext === 'pdf') return 'PDF';
  if (['mp4', 'avi', 'mov', 'mkv', 'webm'].includes(ext)) return 'Video';
  if (['mp3', 'wav', 'aac', 'flac'].includes(ext)) return 'Audio';
  if (['ppt', 'pptx', 'key'].includes(ext)) return 'Presentation';
  if (['doc', 'docx', 'pages', 'rtf'].includes(ext)) return 'Document';
  if (['xls', 'xlsx', 'numbers', 'csv'].includes(ext)) return 'Spreadsheet';
  return ext.toUpperCase();
};

const ResourcesPage: React.FC = () => {
  const supabase = useMemo(() => getSupabaseClient(), []);
  const [resources, setResources] = useState<Resource[]>([]);
  const [userId, setUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [editingResource, setEditingResource] = useState<Resource | null>(null);
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    category: '',
    tags: '',
    is_active: true,
    seeker_viewable: false
  });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [cloningResourceId, setCloningResourceId] = useState<string | null>(null);
  const [editFile, setEditFile] = useState<File | null>(null);
  const [deletingResourceId, setDeletingResourceId] = useState<string | null>(null);
  const clearFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const loadResources = useCallback(async () => {
    try {
      setLoading(true);
      const { data: sessionData } = await supabase.auth.getSession();
      const currentUserId = sessionData?.session?.user?.id ?? null;
      setUserId(currentUserId);

      const { data, error } = await supabase
        .from('resources')
        .select('*')
        .order('uploaded_at', { ascending: false });

      if (error) throw error;

      const mapped = (data || []).map((resource) => {
        const publicUrl = supabase.storage.from('resources').getPublicUrl(resource.file_path).data.publicUrl;
        return {
          ...resource,
          content_url: resource.content_url ?? publicUrl
        } as Resource;
      });

      setResources(mapped);
    } catch (err) {
      console.error('Failed to load resources', err);
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  useEffect(() => {
    loadResources();
  }, [loadResources]);

  const handleFileUpload = async (files: FileList) => {
    if (!files.length) return;

    setUploading(true);
    try {
      const env = (window as any).__ENV__;
      if (!env?.EDGE_BASE) {
        throw new Error('Missing EDGE_BASE configuration');
      }

      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;

      if (!accessToken) {
        throw new Error('You must be signed in to upload files');
      }

      for (const file of Array.from(files)) {
        const formData = new FormData();
        formData.append('file', file, file.name);
        formData.append('name', file.name.replace(/\.[^/.]+$/, ''));

        const response = await fetch(`${env.EDGE_BASE}/upload-resource`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`
          },
          body: formData
        });

        if (!response.ok) {
          const errorBody = await response.text();
          throw new Error(errorBody || response.statusText);
        }
      }

      clearFileInput();
      await loadResources();
    } catch (error) {
      console.error('Failed to upload resource', error);
      alert('Failed to upload resource. Please try again.');
    } finally {
      clearFileInput();
      setUploading(false);
    }
  };

  const openEditModal = (resource: Resource) => {
    if (userId && resource.uploaded_by !== userId) {
      return;
    }
    setEditError(null);
    setEditingResource(resource);
    setEditForm({
      name: resource.name ?? '',
      description: resource.description ?? '',
      category: resource.category ?? '',
      tags: resource.tags?.join(', ') ?? '',
      is_active: resource.is_active,
      seeker_viewable: resource.seeker_viewable ?? false
    });
    setEditFile(null);
  };

  const closeEditModal = () => {
    if (editSaving) return;
    setEditingResource(null);
    setEditError(null);
    setEditForm({
      name: '',
      description: '',
      category: '',
      tags: '',
      is_active: true,
      seeker_viewable: false
    });
    setEditFile(null);
  };

  const handleEditFieldChange = (
    field: 'name' | 'description' | 'category' | 'tags' | 'is_active' | 'seeker_viewable',
    value: string | boolean
  ) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleUpdateResource = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editingResource || (userId && editingResource.uploaded_by !== userId)) {
      return;
    }

    const trimmedName = editForm.name.trim();
    if (!trimmedName) {
      setEditError('Name is required.');
      return;
    }

    setEditSaving(true);
    setEditError(null);

    const parsedTags = editForm.tags
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0);

    const payload: Record<string, any> = {
      name: trimmedName,
      description: editForm.description.trim() || null,
      category: editForm.category.trim() || null,
      tags: parsedTags.length ? parsedTags : null,
      is_active: editForm.is_active,
      seeker_viewable: editForm.seeker_viewable
    };

    let uploadedFilePath: string | null = null;

    try {
      if (editFile && userId) {
        const objectKey = `${userId}/${Date.now()}-${editFile.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
        const { error: uploadError } = await supabase.storage
          .from('resources')
          .upload(objectKey, editFile, {
            cacheControl: '3600',
            upsert: false,
            contentType: editFile.type || 'application/octet-stream'
          });

        if (uploadError) {
          throw new Error(`Failed to upload replacement file: ${uploadError.message}`);
        }

        uploadedFilePath = objectKey;
        payload.file_path = objectKey;
        payload.file_name = editFile.name;
        payload.file_size = editFile.size;
        payload.mime_type = editFile.type || 'application/octet-stream';
        payload.file_type = determineFileType(editFile.name);
        payload.content_url = supabase.storage.from('resources').getPublicUrl(objectKey).data.publicUrl;
      }

      const { error } = await supabase
        .from('resources')
        .update(payload)
        .eq('id', editingResource.id)
        .eq('uploaded_by', editingResource.uploaded_by)
        .select('id')
        .single();

      if (error) {
        throw error;
      }

      await loadResources();
      if (uploadedFilePath && editingResource.file_path !== uploadedFilePath) {
        await supabase.storage.from('resources').remove([editingResource.file_path]).catch(() => undefined);
      }
      closeEditModal();
    } catch (error) {
      console.error('Failed to update resource', error);
      setEditError(error instanceof Error ? error.message : 'Failed to update resource');
      if (uploadedFilePath) {
        await supabase.storage.from('resources').remove([uploadedFilePath]).catch(() => undefined);
      }
    } finally {
      setEditSaving(false);
    }
  };

  const getFileIcon = (fileType: string) => {
    const type = fileType?.toLowerCase();
    switch (type) {
      case 'pdf':
        return <DocumentIcon className="w-5 h-5 text-[#2C2731]" />;
      case 'doc':
      case 'docx':
      case 'document':
        return <DocumentTextIcon className="w-5 h-5 text-[#2C2731]" />;
      case 'ppt':
      case 'pptx':
      case 'presentation':
        return <PresentationChartLineIcon className="w-5 h-5 text-[#2C2731]" />;
      case 'xls':
      case 'xlsx':
      case 'spreadsheet':
        return <DocumentIcon className="w-5 h-5 text-[#2C2731]" />;
      case 'video':
        return <PresentationChartLineIcon className="w-5 h-5 text-[#2C2731]" />;
      case 'audio':
        return <DocumentIcon className="w-5 h-5 text-[#2C2731]" />;
      default:
        return <DocumentIcon className="w-5 h-5 text-[#2C2731]" />;
    }
  };

  const handleUpload = () => {
    fileInputRef.current?.click();
  };

  const handleDownload = async (resource: Resource) => {
    try {
      const { data, error } = await supabase.storage
        .from('resources')
        .download(resource.file_path);

      if (error) throw error;

      const url = window.URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = resource.file_name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      await supabase
        .from('resources')
        .update({ downloads: (resource.downloads ?? 0) + 1 })
        .eq('id', resource.id);

      await loadResources();
    } catch (error) {
      console.error('Failed to download resource', error);
      alert('Failed to download resource. Please try again.');
    }
  };

  const handleUseTemplate = async (resource: Resource) => {
    if (!userId) {
      alert('Please sign in to use templates.');
      return;
    }

    if (resource.uploaded_by === userId) {
      if (resource.content_url) {
        window.open(resource.content_url, '_blank', 'noopener,noreferrer');
      } else {
        handleDownload(resource);
      }
      return;
    }

    const sanitizedName = resource.file_name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const newPath = `${userId}/${Date.now()}-${sanitizedName}`;
    setCloningResourceId(resource.id);

    try {
      let copyPerformed = false;
      const { error: copyError } = await supabase.storage
        .from('resources')
        .copy(resource.file_path, newPath);

      if (!copyError) {
        copyPerformed = true;
      } else {
        const { data: downloadData, error: downloadError } = await supabase.storage
          .from('resources')
          .download(resource.file_path);

        if (downloadError || !downloadData) {
          throw downloadError || new Error('Failed to download template file');
        }

        const buffer = await downloadData.arrayBuffer();
        const { error: uploadError } = await supabase.storage
          .from('resources')
          .upload(newPath, buffer, {
            cacheControl: '3600',
            upsert: false,
            contentType: resource.mime_type || 'application/octet-stream'
          });

        if (uploadError) {
          throw uploadError;
        }
      }

      const [{ data: profileData }, { data: authUserData }] = await Promise.all([
        supabase
          .from('profiles')
          .select('full_name')
          .eq('id', userId)
          .maybeSingle(),
        supabase.auth.getUser()
      ]);

      const authorName = profileData?.full_name
        || authUserData?.user?.user_metadata?.full_name
        || authUserData?.user?.email
        || 'Guide';

      const publicUrl = supabase.storage.from('resources').getPublicUrl(newPath).data.publicUrl;

      const { error: insertError } = await supabase
        .from('resources')
        .insert({
          name: resource.name,
          description: resource.description,
          author: authorName,
          category: resource.category,
          tags: resource.tags,
          file_name: resource.file_name,
          file_path: newPath,
          file_size: resource.file_size,
          file_type: resource.file_type ?? determineFileType(resource.file_name),
          mime_type: resource.mime_type,
          content_url: publicUrl,
          downloads: 0,
          is_active: true,
          uploaded_by: userId,
          parent_id: resource.id
        });

      if (insertError) {
        throw insertError;
      }

      await loadResources();
      alert('Template added to your resources. You can edit it now.');
    } catch (error) {
      console.error('Failed to add template', error);
      alert('Failed to add template to your resources. Please try again.');
      await supabase.storage.from('resources').remove([newPath]).catch(() => undefined);
    } finally {
      setCloningResourceId(null);
    }
  };

  const handleDeleteResource = async (resource: Resource) => {
    if (!userId || resource.uploaded_by !== userId) {
      return;
    }

    if (!confirm(`Delete “${resource.name}”? This cannot be undone.`)) {
      return;
    }

    setDeletingResourceId(resource.id);
    try {
      const { error } = await supabase
        .from('resources')
        .delete()
        .eq('id', resource.id)
        .eq('uploaded_by', userId);

      if (error) {
        throw error;
      }

      await supabase.storage.from('resources').remove([resource.file_path]).catch(() => undefined);
      await loadResources();
    } catch (error) {
      console.error('Failed to delete resource', error);
      alert('Failed to delete resource. Please try again.');
    } finally {
      setDeletingResourceId(null);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileTypeLabel = (resource: Resource) => resource.file_type || resource.mime_type?.split('/').pop()?.toUpperCase() || 'Document';

  const myDocuments = userId ? resources.filter((resource) => resource.uploaded_by === userId) : [];
  const communityTemplates = resources
    .filter((resource) => !userId || resource.uploaded_by !== userId)
    .sort((a, b) => (b.downloads ?? 0) - (a.downloads ?? 0))
    .slice(0, 10);

  const formatUploadedDate = (date?: string) => {
    if (!date) return 'Unknown';
    return new Date(date).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="p-6 bg-[#FFFCEF] text-[#2C2731] min-h-full">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-center items-center h-64">
            <div className="text-[#2C2731]/70">Loading resources...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-[#FFFCEF] text-[#2C2731] min-h-full">
      <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-medium font-recoleta text-[#2C2731] mb-1">Resources</h1>
        <p className="text-sm text-[#2C2731]/80">Upload and manage your practice documents and templates</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* My Documents Section */}
        <div className="bg-[#FFFCEF] border border-[#2C2731] rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-[#2C2731] bg-[#2C2731] flex items-center justify-between text-[#FFFCEF]">
            <h2 className="text-base font-medium font-recoleta">My Documents</h2>
            <div className="flex items-center gap-3">
              <input
                type="file"
                ref={fileInputRef}
                multiple
                className="hidden"
                onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
              />
              <button
                onClick={handleUpload}
                className="inline-flex items-center px-3 py-1.5 border border-[#FFFCEF] bg-[#FFFCEF] text-[#2C2731] text-sm font-medium rounded-md hover:bg-[#F3EBCD] transition-colors disabled:opacity-60"
                disabled={uploading}
              >
                <PlusIcon className="w-4 h-4 mr-1" />
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>

          <div className="divide-y divide-[#2C2731]/20">
            {myDocuments.length === 0 ? (
              <div className="px-6 py-6 text-sm text-[#2C2731]/70">
                You haven’t uploaded any documents yet.
              </div>
            ) : myDocuments.map((doc) => (
              <div key={doc.id} className="px-6 py-4 flex items-center justify-between hover:bg-[#F3EBCD] transition-colors">
                <div className="flex items-center space-x-3 flex-1">
                  {getFileIcon(getFileTypeLabel(doc))}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-[#2C2731] truncate">
                        {doc.name}
                      </p>
                      {!doc.is_active && (
                        <span className="inline-flex items-center rounded-full bg-[#2C2731]/10 px-2 py-0.5 text-[10px] font-semibold uppercase text-[#2C2731]">
                          Inactive
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[#2C2731]/70">
                      {getFileTypeLabel(doc)} • {formatFileSize(doc.file_size)} • Uploaded {new Date(doc.uploaded_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => openEditModal(doc)}
                    className="p-1.5 text-[#2C2731]/60 hover:text-[#2C2731] transition-colors"
                    title="Edit"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232a2.5 2.5 0 113.536 3.536L7.5 20.036 3 21l.964-4.5 11.268-11.268z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleDownload(doc)}
                    className="p-1.5 text-[#2C2731]/60 hover:text-[#2C2731] transition-colors"
                    title="Download"
                  >
                    <ArrowDownTrayIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteResource(doc)}
                    className="p-1.5 text-[#2C2731]/60 hover:text-[#2C2731] transition-colors"
                    title="Delete"
                    disabled={deletingResourceId === doc.id}
                  >
                    {deletingResourceId === doc.id ? (
                      <svg className="w-4 h-4 animate-spin text-[#2C2731]" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <circle cx="12" cy="12" r="10" strokeWidth="4" className="opacity-25" />
                        <path d="M4 12a8 8 0 018-8" strokeWidth="4" strokeLinecap="round" className="opacity-75" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="px-6 py-4 border-t border-[#2C2731]/20 text-center">
            <button className="text-sm text-[#2C2731] hover:text-[#241F2A] transition-colors">
              View All Documents ({myDocuments.length})
            </button>
          </div>
        </div>

        {/* Guide Collective Templates Section */}
        <div className="bg-[#FFFCEF] border border-[#2C2731] rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-[#2C2731] bg-[#2C2731] text-[#FFFCEF] flex items-center justify-between">
            <div>
              <h2 className="text-base font-medium font-recoleta">Guide Collective Templates</h2>
              <p className="text-xs text-[#FFFCEF]/80 mt-1">Community</p>
            </div>
          </div>

          <div className="divide-y divide-[#2C2731]/20">
            {communityTemplates.length === 0 ? (
              <div className="px-6 py-6 text-sm text-[#2C2731]/70">
                No shared templates available yet.
              </div>
            ) : communityTemplates.map((template) => (
              <div key={template.id} className="px-6 py-4 flex items-center justify-between hover:bg-[#F3EBCD] transition-colors">
                <div className="flex items-center space-x-3 flex-1">
                  {getFileIcon(getFileTypeLabel(template))}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#2C2731] truncate">
                      {template.name}
                    </p>
                    <p className="text-xs text-[#2C2731]/70">
                      By {template.author || 'GuidesCollective'} • {template.downloads ?? 0} downloads
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleUseTemplate(template)}
                  disabled={cloningResourceId === template.id}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    cloningResourceId === template.id
                      ? 'bg-[#2C2731] text-[#FFFCEF] opacity-60 cursor-not-allowed'
                      : 'bg-[#2C2731] text-[#FFFCEF] hover:bg-[#241F2A]'
                  }`}
                >
                  {cloningResourceId === template.id ? 'Adding...' : 'Use Template'}
                </button>
              </div>
            ))}
          </div>

          <div className="px-6 py-4 border-t border-[#2C2731]/20 text-center">
            <button className="inline-flex items-center text-sm text-[#2C2731] hover:text-[#241F2A] transition-colors">
              <MagnifyingGlassIcon className="w-4 h-4 mr-1" />
              Browse All Templates
            </button>
          </div>
        </div>
      </div>

      {editingResource && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-lg rounded-lg border border-[#2C2731] bg-[#FFFCEF] p-6">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-lg font-semibold text-[#2C2731]">Edit Resource</h2>
                <p className="text-sm text-[#2C2731]/70">Update details for "{editingResource.name}"</p>
              </div>
              <button
                type="button"
                onClick={closeEditModal}
                className="text-[#2C2731]/60 hover:text-[#2C2731]"
              >
                <span className="sr-only">Close</span>
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {editError && (
              <div className="mt-4 rounded-lg border border-red-300 bg-red-100 px-3 py-2 text-sm text-red-700">
                {editError}
              </div>
            )}

            <form onSubmit={handleUpdateResource} className="mt-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#2C2731] mb-1" htmlFor="guide-resource-name">
                  Name
                </label>
                <input
                  id="guide-resource-name"
                  type="text"
                  value={editForm.name}
                  onChange={event => handleEditFieldChange('name', event.target.value)}
                  className="w-full rounded-md border border-[#2C2731] bg-[#FFFCEF] px-3 py-2 text-sm focus:border-[#2C2731] focus:outline-none focus:ring-1 focus:ring-[#2C2731]"
                  placeholder="Resource name"
                  disabled={editSaving}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#2C2731] mb-1" htmlFor="guide-resource-description">
                  Description
                </label>
                <textarea
                  id="guide-resource-description"
                  value={editForm.description}
                  onChange={event => handleEditFieldChange('description', event.target.value)}
                  rows={3}
                  className="w-full rounded-md border border-[#2C2731] bg-[#FFFCEF] px-3 py-2 text-sm focus:border-[#2C2731] focus:outline-none focus:ring-1 focus:ring-[#2C2731]"
                  placeholder="Brief description"
                  disabled={editSaving}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#2C2731] mb-1" htmlFor="guide-resource-category">
                  Category
                </label>
                <input
                  id="guide-resource-category"
                  type="text"
                  value={editForm.category}
                  onChange={event => handleEditFieldChange('category', event.target.value)}
                  className="w-full rounded-md border border-[#2C2731] bg-[#FFFCEF] px-3 py-2 text-sm focus:border-[#2C2731] focus:outline-none focus:ring-1 focus:ring-[#2C2731]"
                  placeholder="Category"
                  disabled={editSaving}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#2C2731] mb-1" htmlFor="guide-resource-tags">
                  Tags (comma separated)
                </label>
                <input
                  id="guide-resource-tags"
                  type="text"
                  value={editForm.tags}
                  onChange={event => handleEditFieldChange('tags', event.target.value)}
                  className="w-full rounded-md border border-[#2C2731] bg-[#FFFCEF] px-3 py-2 text-sm focus:border-[#2C2731] focus:outline-none focus:ring-1 focus:ring-[#2C2731]"
                  placeholder="e.g. Meditation, Journaling"
                  disabled={editSaving}
                />
              </div>

              <div className="flex items-center justify-between rounded-md border border-[#2C2731]/40 bg-[#E8DFBD] px-3 py-2">
                <div>
                  <p className="text-sm font-medium text-[#2C2731]">Visibility</p>
                  <p className="text-xs text-[#2C2731]/70">Inactive resources are hidden from seekers.</p>
                </div>
                <label className="inline-flex items-center gap-2 text-sm text-[#2C2731]">
                  <input
                    type="checkbox"
                    checked={editForm.is_active}
                    onChange={event => handleEditFieldChange('is_active', event.target.checked)}
                    disabled={editSaving}
                    className="h-4 w-4 accent-[#2C2731]"
                  />
                  Active
                </label>
              </div>

              <div className="flex items-center justify-between rounded-md border border-[#2C2731]/40 bg-[#E8DFBD] px-3 py-2">
                <div>
                  <p className="text-sm font-medium text-[#2C2731]">Seeker Access</p>
                  <p className="text-xs text-[#2C2731]/70">Allow seekers to view this resource.</p>
                </div>
                <label className="inline-flex items-center gap-2 text-sm text-[#2C2731]">
                  <input
                    type="checkbox"
                    checked={editForm.seeker_viewable}
                    onChange={event => handleEditFieldChange('seeker_viewable', event.target.checked)}
                    disabled={editSaving}
                    className="h-4 w-4 accent-[#2C2731]"
                  />
                  Viewable
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#2C2731] mb-1" htmlFor="guide-resource-file">
                  Replace file (optional)
                </label>
                <input
                  id="guide-resource-file"
                  type="file"
                  onChange={(event) => setEditFile(event.target.files && event.target.files[0] ? event.target.files[0] : null)}
                  className="w-full text-sm text-[#2C2731]"
                  disabled={editSaving}
                  accept=".pdf,.doc,.docx,.ppt,.pptx,.mp4,.mp3,.wav"
                />
                <p className="mt-1 text-xs text-[#2C2731]/70">
                  Current file: {editingResource.file_name}. Leave blank to keep existing file.
                </p>
                {editFile && (
                  <p className="mt-1 text-xs text-[#2C2731]">
                    Selected: {editFile.name} ({formatFileSize(editFile.size)})
                  </p>
                )}
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="rounded-md border border-[#2C2731] px-4 py-2 text-sm font-medium text-[#2C2731] hover:bg-[#F3EBCD] disabled:opacity-60"
                  disabled={editSaving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-md border border-[#2C2731] bg-[#2C2731] px-4 py-2 text-sm font-medium text-[#FFFCEF] hover:bg-[#241F2A] disabled:opacity-70"
                  disabled={editSaving}
                >
                  {editSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  </div>
);
};

export default ResourcesPage;
